import React from 'react';
import { Helmet } from 'react-helmet-async';

const BASE_URL = 'https://dopove.com';
const DEFAULT_IMAGE = `${BASE_URL}/logo_new_16x9_trans_inverted.png`;
const ORGANIZATION = {
  name: 'Dopove',
  url: BASE_URL,
  logo: DEFAULT_IMAGE,
  sameAs: [
    'https://x.com/dopove',
    'https://peerlist.io/dopove',
    'https://github.com/dopove',
    'https://linkedin.com/company/dopove'
  ],
  foundingDate: '2024',
  description: 'Dopove builds useful memory for AI agents and applies systems thinking to how it works. Its first product is ICE, the Indexless Context Engine.'
};

export const SEO = ({
  title,
  description,
  canonical,
  ogImage,
  ogType = 'website',
  twitterCard = 'summary_large_image',
  jsonLd,
  breadcrumbs,
  noIndex = false,
  noFollow = false
}) => {
  const fullTitle = title ? `${title} | Dopove` : 'Dopove: useful memory for AI agents';
  const fullDescription = description || 'Dopove builds useful memory for AI agents and applies systems thinking to how it works. Its first product is ICE, the Indexless Context Engine.';
  const fullCanonical = canonical || BASE_URL;
  const fullOgImage = ogImage || 'https://dopove.com/logo_new_16x9_trans_inverted.png';

  const structuredData = [];

  // Always include Organization
  structuredData.push({
    '@context': 'https://schema.org',
    '@type': 'Organization',
    ...ORGANIZATION
  });

  // WebSite with SearchAction on all pages
  structuredData.push({
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    url: BASE_URL,
    name: 'Dopove',
    description: 'Dopove builds useful memory for AI agents and applies systems thinking to how it works. Its first product is ICE, the Indexless Context Engine.',
    publisher: { '@type': 'Organization', name: 'Dopove' }
  });

  // BreadcrumbList if provided
  if (breadcrumbs && breadcrumbs.length > 0) {
    const breadcrumbJsonLd = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: breadcrumbs.map((crumb, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: crumb.label,
        item: crumb.href ? `${BASE_URL}${crumb.href}` : undefined
      })).filter(c => c.item)
    };
    structuredData.push(breadcrumbJsonLd);
  }

  // Page-specific JSON-LD
  if (jsonLd) {
    if (Array.isArray(jsonLd)) {
      structuredData.push(...jsonLd);
    } else {
      structuredData.push(jsonLd);
    }
  }

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={fullDescription} />
      <meta name="viewport" content="width=device-width, initial-scale=1" />
      <meta name="theme-color" content="#0A0F1A" />

      {noIndex && <meta name="robots" content="noindex" />}
      {noFollow && <meta name="robots" content="nofollow" />}
      {(noIndex || noFollow) && <meta name="robots" content={`${noIndex ? 'noindex' : ''} ${noFollow ? 'nofollow' : ''}`.trim()} />}

      <link rel="canonical" href={fullCanonical} />

      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={fullDescription} />
      <meta property="og:image" content={fullOgImage} />
      <meta property="og:url" content={fullCanonical} />
      <meta property="og:type" content={ogType} />
      <meta property="og:site_name" content="Dopove" />

      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={fullDescription} />
      <meta name="twitter:image" content={fullOgImage} />
      <meta name="twitter:site" content="@dopove" />
      <meta name="twitter:creator" content="@dopove" />


      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify({ '@context': 'https://schema.org', '@graph': structuredData }) }}
      />
    </Helmet>
  );
};

export default SEO;